import type { FC } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { withFormik } from 'formik';

import { usePrevious } from 'src/hooks';
import type { PossibleOptions } from 'src/types/reportConfig';
import { useSection } from 'src/components/app/context/SectionContext';
import ErrorMessage from 'src/components/common/Messages/ErrorMessage';
import { useReportConfigAllocationCombinationQuery } from 'src/components/reportConfig/hooks/useReportConfigAllocationQuery';
import { useSurveyDetailQuery } from 'src/components/survey/SurveySettings/api';
import { getPossibleOptions } from 'src/components/workspace/helper/helper';

import OrderedReportsList, { reportKey } from './OrderedReportsList';

import { styles } from './index.styles';

export const CONFIGURE_REPORT_FORM_TEST_ID = 'configure-report-form';
export const SELECT_REPORT_TYPE_NAME_INPUT = 'select-report-type-name-input';

const ReportIsInList = (object: any, list: any[]) => {
  const compareString = reportKey(object);

  return !!list.find((element) => reportKey(element) === compareString);
};

export interface ConfigureReportFormProps {
  errors?: any;
  isSubmitting?: any;
  setSubmitting?: any;
  isValid?: any;
  handleChange?: any;
  handleBlur?: any;
  handleInputBlur?: any;
  handleSubmit?: any;
  reportNameOnly?: any;
  setFieldValue?: any;
  submitButtonText?: any;
  setReportConfiguration?: any;
  afterSubmit?: any;
  touched?: any;
  validateForm?: any;
  values?: any;
  getDriverAnalysisSet?: any;
}

const ConfigureReportForm: FC<ConfigureReportFormProps> = ({
  errors = null,
  isSubmitting,
  setSubmitting,
  isValid,
  handleChange,
  handleBlur,
  handleInputBlur,
  handleSubmit,
  reportNameOnly = false,
  setFieldValue,
  submitButtonText,
  setReportConfiguration,
  afterSubmit,
  touched = null,
  validateForm,
  values = null,
}) => {
  const { t } = useTranslation([
    'workspace',
    'report',
    'language',
    'reportType',
  ]);

  const { moduleId } = useSection();
  const previousReportType = usePrevious(values.reportType);
  const { data: reportConfigData, loading: loadingReportConfigData } =
    useReportConfigAllocationCombinationQuery(moduleId);
  const [possibleOptions, setPossibleOptions] = useState<PossibleOptions>(
    getPossibleOptions(
      String(values.reportType),
      String(values.fileType),
      reportConfigData?.reportConfigCombinations,
    ),
  );
  const [orderedReports, setOrderedReports] = useState<any[]>([]);

  // Make sure the loaded configuration is valid.
  useEffect(() => {
    validateForm();
  }, [validateForm]);

  // Manually set conditional field values when the report type has changed as formik won't register value changes
  // based on re-rendering.
  useEffect(() => {
    // Setting and Rendering the initial possible options will trigger this update. We don't want those to be
    // overwritten, so we exit if there is no previous state.
    if (typeof previousReportType === 'undefined') return;

    const newPossibleOptions: any = getPossibleOptions(
      String(values.reportType),
      String(values.fileType),
      reportConfigData?.reportConfigCombinations,
    );

    setPossibleOptions(newPossibleOptions);

    const fileTypeKeys = Object.keys(newPossibleOptions.fileTypes).map(Number);
    if (
      fileTypeKeys.length > 0 &&
      !fileTypeKeys.includes(Number(values.fileType))
    )
      setFieldValue('fileType', fileTypeKeys[0]);

    const languageKeys = Object.keys(newPossibleOptions.languages).map(Number);
    if (
      languageKeys.length > 0 &&
      !languageKeys.includes(Number(values.language))
    )
      setFieldValue('language', languageKeys[0]);
  }, [
    previousReportType,
    setFieldValue,
    values.fileType,
    values.language,
    values.reportType,
    reportConfigData,
  ]);

  const reportTypeEntries = useMemo(
    () =>
      possibleOptions.reportTypes
        ? Object.entries(possibleOptions.reportTypes)
        : [],
    [possibleOptions.reportTypes],
  );
  const languageEntries = useMemo(
    () =>
      possibleOptions.languages
        ? Object.entries(possibleOptions.languages)
        : [],
    [possibleOptions.languages],
  );
  const fileTypeEntries = useMemo(
    () =>
      possibleOptions.fileTypes
        ? Object.entries(possibleOptions.fileTypes)
        : [],
    [possibleOptions.fileTypes],
  );

  const reportConfigEntriesExist =
    !!reportTypeEntries.length &&
    !!languageEntries.length &&
    !!fileTypeEntries.length;

  const reportIsInList = useMemo(
    () => ReportIsInList(values, orderedReports),
    [values, orderedReports],
  );

  const handleSubmitAndContinue = () => {
    setSubmitting(true);
    const report = { ...values };
    setReportConfiguration(report, () => {
      afterSubmit(true).then(() => {
        setOrderedReports((prev) => [...prev, report]);
        setSubmitting(false);
      });
    });
  };

  return (
    <form onSubmit={handleSubmit} data-test-id={CONFIGURE_REPORT_FORM_TEST_ID}>
      <Stack
        direction="column"
        spacing={2}
        sx={{
          pt: 2,
        }}
      >
        <label htmlFor="reportName">{t('report:reportInfo.name')}</label>

        <OutlinedInput
          autoFocus
          data-test-id={SELECT_REPORT_TYPE_NAME_INPUT}
          label=""
          type="text"
          name="reportName"
          onChange={handleChange}
          onBlur={(event) => handleInputBlur(event, handleBlur)}
          value={values.reportName}
        />

        {errors.reportName && touched.reportName && (
          <ErrorMessage align="left">{errors.reportName}</ErrorMessage>
        )}

        {!reportNameOnly &&
        !loadingReportConfigData &&
        reportConfigEntriesExist ? (
          <Box>
            {/*report type selection*/}
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <label htmlFor="reportSelectionField">
                  {t('report:reportInfo.type')}
                </label>

                <Select
                  id="reportSelectionField"
                  name="reportType"
                  value={values.reportType}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                >
                  {reportTypeEntries.map(([reportTypeCode, reportTypeName]) => (
                    <MenuItem key={reportTypeCode} value={reportTypeCode}>
                      {reportTypeName}
                    </MenuItem>
                  ))}
                </Select>

                {errors.reportType && (
                  <ErrorMessage align="left">{errors.reportType}</ErrorMessage>
                )}
              </Grid>

              {/*language selection*/}
              <Grid item xs={4}>
                <label htmlFor="languageSelectionField">
                  {t('report:reportInfo.language')}
                </label>

                <Select
                  id="languageSelectionField"
                  name="language"
                  value={values.language}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                >
                  {languageEntries.map(
                    ([languageCode, languageAbbreviation]) => (
                      <MenuItem key={languageCode} value={languageCode}>
                        {languageAbbreviation}
                      </MenuItem>
                    ),
                  )}
                </Select>
              </Grid>

              {/*file type selection*/}
              <Grid item xs={4}>
                <label htmlFor="fileTypeSelectionField">
                  {t('report:reportInfo.fileType')}
                </label>

                <Select
                  id="fileTypeSelectionField"
                  name="fileType"
                  value={values.fileType}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                >
                  {fileTypeEntries.map(([fileTypeCode, fileTypeName]) => (
                    <MenuItem key={fileTypeCode} value={fileTypeCode}>
                      {fileTypeName}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>

            <div>
              <p>{possibleOptions?.comment}</p>
            </div>

            <OrderedReportsList
              reports={orderedReports as any}
              reportConfigCombinations={
                reportConfigData?.reportConfigCombinations
              }
            />
            <Typography hidden={!reportIsInList} sx={styles.warning}>
              {t('reportForm.reportAlreadyOrdered')}
            </Typography>
          </Box>
        ) : !reportNameOnly && loadingReportConfigData ? (
          <Stack
            direction="row"
            sx={{
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={20} />
          </Stack>
        ) : !reportNameOnly &&
          !loadingReportConfigData &&
          !reportConfigEntriesExist ? (
          <Stack
            direction="row"
            sx={{
              justifyContent: 'center',
            }}
          >
            {t('reportForm.noReportTypesAvailable')}
          </Stack>
        ) : null}

        <Stack
          direction="row"
          sx={{
            justifyContent: 'center',
          }}
        >
          {!reportNameOnly && (
            <LoadingButton
              variant="cta"
              color="secondary"
              loading={isSubmitting}
              disabled={
                !isValid ||
                reportIsInList ||
                loadingReportConfigData ||
                !reportConfigEntriesExist
              }
              onClick={handleSubmitAndContinue}
            >
              {t('reportForm.orderAndContinue')}
            </LoadingButton>
          )}
          <LoadingButton
            variant="cta"
            type="submit"
            loading={isSubmitting}
            disabled={
              !isValid ||
              reportIsInList ||
              !reportConfigEntriesExist ||
              (reportNameOnly && loadingReportConfigData)
            }
            onClick={handleSubmit}
          >
            {submitButtonText}
          </LoadingButton>
        </Stack>
      </Stack>
    </form>
  );
};

interface ConfigureReportFormWrapperProps {
  afterSubmit: any;
  closeModal: any;
  currentValues: any;
  handleInputBlur: any;
  reportNameOnly: any;
  setReportConfiguration: any;
  submitButtonText: any;
  getDriverAnalysisSet: any;
  survey?: any;
}

const ConfigureReportFormWithFormik = withTranslation('workspace')(
  withFormik({
    mapPropsToValues: ({ currentValues }: ConfigureReportFormWrapperProps) => ({
      language: currentValues.language,
      fileType: currentValues.fileType,
      reportName: currentValues.reportName,
      reportType: currentValues.reportType,
    }),

    validate: (values, { t }: any) => {
      const errors: any = {};

      if (!values.reportName) {
        errors.reportName = t('reportForm.nameRequired');
      }

      return errors;
    },

    handleSubmit: (values, { props, setSubmitting }) => {
      const { setReportConfiguration, closeModal, afterSubmit } = props;

      setSubmitting(true);
      setReportConfiguration(values, () => afterSubmit().then(closeModal));
    },

    displayName: 'ConfigureReportForm',
  })(ConfigureReportForm),
);

const ConfigureReportFormWithHooks: FC<ConfigureReportFormWrapperProps> = (
  props,
) => {
  const { data: survey } = useSurveyDetailQuery();
  return <ConfigureReportFormWithFormik {...props} survey={survey} />;
};

export default ConfigureReportFormWithHooks;
